/**
 * What the portal knows about an asset, shared by the computer's library and the phone's: its kind, its
 * size line, whether it can be shown inline, the partner's own uploads (list, upload, delete) and the
 * fetched bytes for a preview. Brand assets are shown at full fidelity, never downscaled.
 */
import { useCallback, useEffect, useState } from "react";
import type { Asset, PartnerOwnAsset } from "../../shared/types";
import { partnerApi, PartnerApiError } from "./api";
import { humanSize } from "./format";

export function typeLabel(type: Asset["type"]): string {
  if (type === "video") return "Video";
  if (type === "image") return "Image";
  if (type === "copy") return "Copy";
  return "File";
}

export function extOf(asset: Asset): string {
  const fn = asset.filename ?? "";
  const dot = fn.lastIndexOf(".");
  if (dot >= 0) return fn.slice(dot + 1).toLowerCase();
  const sub = asset.contentType?.split("/")[1];
  return sub ? sub.toLowerCase() : "";
}

/** The mono sub-line under an asset name: "png 512 KB" for files, a word count for copy. */
export function metaSub(asset: Asset): string {
  if (asset.type === "copy") {
    const words = (asset.text ?? "").trim().split(/\s+/).filter(Boolean).length;
    return `${words} ${words === 1 ? "word" : "words"}`;
  }
  return [extOf(asset), humanSize(asset.sizeBytes)].filter(Boolean).join(" · ");
}

/** Whether an asset can be previewed inline (image, video, or a PDF file). */
export function canPreview(asset: Asset): boolean {
  if (asset.type === "image" || asset.type === "video" || asset.type === "copy") return true;
  return asset.contentType === "application/pdf" || extOf(asset) === "pdf";
}

/** The extension shown for an own asset, derived from its content type. */
function ownExtOf(asset: PartnerOwnAsset): string {
  const sub = asset.contentType?.split("/")[1];
  return sub ? sub.toLowerCase() : "";
}

/** The mono sub-line under an own asset name: "png 512 KB". */
export function ownMetaSub(asset: PartnerOwnAsset): string {
  return [ownExtOf(asset), humanSize(asset.sizeBytes)].filter(Boolean).join(" · ");
}

/** Whether an own asset can be previewed inline (image, video, or a PDF). */
export function ownCanPreview(asset: PartnerOwnAsset): boolean {
  if (asset.type === "image" || asset.type === "video") return true;
  return asset.contentType === "application/pdf";
}

/** Adapt an own asset to the shared `Asset` shape so it can reuse the preview. */
export function ownAssetToAsset(asset: PartnerOwnAsset): Asset {
  return {
    id: asset.id,
    productId: "",
    folderId: null,
    name: asset.name,
    type: asset.type,
    text: null,
    contentType: asset.contentType,
    sizeBytes: asset.sizeBytes,
    filename: null,
    sharedWithPartners: false,
    partnerVisible: true,
    createdAt: asset.createdAt,
  };
}

/**
 * The partner's OWN uploads for one program: the list, a real upload to R2 with live progress, and delete.
 * Only the owning partner ever sees these. `upload` and `remove` resolve true when they worked.
 */
export function useOwnAssets(programId: string) {
  const [assets, setAssets] = useState<PartnerOwnAsset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const view = await partnerApi.myAssets(programId);
      setAssets(view.assets);
    } catch (err) {
      setAssets([]);
      setError(err instanceof PartnerApiError ? err.message : "We could not load your uploads.");
    }
  }, [programId]);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = useCallback(
    async (file: File): Promise<boolean> => {
      setError(null);
      setUploading(true);
      setProgress(0);
      try {
        const { asset } = await partnerApi.uploadMyAsset(programId, file, file.name, setProgress);
        setAssets((cur) => [asset, ...(cur ?? [])]);
        return true;
      } catch (err) {
        setError(err instanceof PartnerApiError ? err.message : "Upload failed. Try again.");
        return false;
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [programId],
  );

  const remove = useCallback(async (id: string): Promise<boolean> => {
    setBusyId(id);
    setError(null);
    try {
      await partnerApi.deleteMyAsset(id);
      setAssets((cur) => (cur ?? []).filter((a) => a.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof PartnerApiError ? err.message : "We could not delete that. Try again.");
      return false;
    } finally {
      setBusyId(null);
    }
  }, []);

  return { assets, error, uploading, progress, busyId, upload, remove };
}

/** The authorized bytes behind a preview, fetched once and shown from a local URL. */
export function usePreview(asset: Asset, downloadUrl: string) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">(asset.type === "copy" ? "ready" : "loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (asset.type === "copy") return;
    let alive = true;
    let objectUrl: string | null = null;
    setState("loading");
    fetch(downloadUrl, { credentials: "same-origin" })
      .then(async (res) => {
        if (!res.ok) {
          throw new PartnerApiError(
            res.status === 404 ? "This file's bytes are not in storage yet." : "We could not load this preview.",
            res.status,
          );
        }
        const blob = await res.blob();
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setState("ready");
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setMsg(e instanceof PartnerApiError ? e.message : "We could not load this preview.");
        setState("error");
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.type, downloadUrl]);

  return { url, state, msg };
}
